import { useId } from 'react';
import { Download, File as FileIcon, FileText, Paperclip } from 'lucide-react';

import { formatBytes, typeLabel, type UploadedFile } from './attachmentFiles';

type UrlOf = (id: string, download?: boolean) => string;

function FileKindIcon({ contentType }: { contentType: string }) {
  return contentType === 'application/pdf' ? <FileIcon aria-hidden="true" /> : <FileText aria-hidden="true" />;
}

/** Saved files in their own section under the text: a rule and an "Attachments" heading
 *  with the count, then images as thumbnails that open full size and other files as named
 *  links. `urlOf` says where a file is read (`download` asks for a download). */
export function AttachmentList({ attachments, urlOf }: { attachments: UploadedFile[]; urlOf: UrlOf }) {
  const headingId = useId();
  if (attachments.length === 0) return null;
  const images = attachments.filter((a) => a.is_image);
  const files = attachments.filter((a) => !a.is_image);
  return (
    <section className="attachments" aria-labelledby={headingId}>
      <h3 className="attachments-head" id={headingId}>
        <Paperclip aria-hidden="true" />
        Attachments
        <span className="count">{attachments.length}</span>
      </h3>
      {images.length > 0 && (
        <ul className="attach-gallery" aria-label="Images">
          {images.map((a) => (
            <li key={a.id}>
              <a href={urlOf(a.id)} target="_blank" rel="noopener noreferrer" title={`${a.filename}, ${formatBytes(a.size_bytes)}`}>
                <img src={urlOf(a.id)} alt={a.filename} loading="lazy" />
              </a>
            </li>
          ))}
        </ul>
      )}
      {files.length > 0 && (
        <ul className="attach-files" aria-label="Files">
          {files.map((a) => (
            <li key={a.id} className="attach-file">
              <FileKindIcon contentType={a.content_type} />
              <a href={urlOf(a.id)} target="_blank" rel="noopener noreferrer">
                {a.filename}
              </a>
              <span className="hint">{formatBytes(a.size_bytes)}</span>
              <a
                href={urlOf(a.id, true)}
                className="btn btn-ghost btn-icon attach-download"
                aria-label={`Download ${a.filename}`}
                data-tooltip="Download"
              >
                <Download aria-hidden="true" />
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Saved files as a table: name (opens in a new tab), type, size and a download button. */
export function AttachmentTable({
  attachments,
  urlOf,
}: {
  attachments: (UploadedFile & { created_at?: string })[];
  urlOf: UrlOf;
}) {
  return (
    <div className="table-scroll">
      <table className="data compact attach-table">
        <thead>
          <tr>
            <th scope="col">File</th>
            <th scope="col">Type</th>
            <th scope="col" className="num">Size</th>
            <th scope="col"><span className="sr-only">Download</span></th>
          </tr>
        </thead>
        <tbody>
          {attachments.map((a) => (
            <tr key={a.id}>
              <td className="attach-table-name">
                <span className="attach-table-file">
                  <FileKindIcon contentType={a.content_type} />
                  <a href={urlOf(a.id)} target="_blank" rel="noopener noreferrer" title={a.filename}>
                    {a.filename}
                  </a>
                </span>
              </td>
              <td className="hint">{typeLabel(a.content_type)}</td>
              <td className="num">{formatBytes(a.size_bytes)}</td>
              <td>
                <div className="row-actions">
                  <a
                    href={urlOf(a.id, true)}
                    className="btn btn-secondary btn-icon"
                    aria-label={`Download ${a.filename}`}
                    data-tooltip="Download"
                  >
                    <Download aria-hidden="true" />
                  </a>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
